import { useState } from 'react';
import { ReactComponent as CheckIcon } from '@assets/icons/check.svg';
import { ReactComponent as ChevronDown } from '@assets/icons/chevron-down.svg';
import { SERVICES_TABS } from 'constant';

import { Box, Collapse, Flex, Stack, Text, useTheme } from '@chakra-ui/react';

import { CategoryInterface, SpecialServicesTabsIds } from 'types';

import { Divider } from 'ui';

interface StepperProps {
  categories: CategoryInterface[];
  specialCategories: CategoryInterface[];
  currentCategory: CategoryInterface;
  onChangeCategory: (category: CategoryInterface) => void;
  showReviewTab: boolean;
}

const ServiceMobileStepper = ({
  categories,
  specialCategories,
  currentCategory,
  onChangeCategory,
  showReviewTab
}: StepperProps) => {
  const {
    colors: {
      palette: {
        booking: {
          stepService: { mobileStepper }
        }
      }
    }
  } = useTheme();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const reviewTab = SERVICES_TABS[SpecialServicesTabsIds.Review];
  const isReviewStep = currentCategory.id === reviewTab.id;

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const onChangeHandler = (value: CategoryInterface) => () => {
    onChangeCategory(value);
    toggleCollapse();
  };

  const lastSpecialCategory = specialCategories?.[specialCategories.length - 1];
  const consultationCategoryName =
    SERVICES_TABS[SpecialServicesTabsIds.Consultation].name.toLowerCase();
  const isConsultationCategoryIncluded = categories.some(
    (category) => category.name.toLowerCase() === consultationCategoryName
  );
  const categoryExtraSpaceName = isConsultationCategoryIncluded
    ? consultationCategoryName
    : lastSpecialCategory?.name.toLowerCase();

  return (
    <Stack bg='white' boxShadow='0px -3px 6px rgba(0, 0, 0, 0.16)' gap={0}>
      <Flex
        onClick={toggleCollapse}
        justifyContent='space-between'
        alignItems='center'
        bg={mobileStepper.collapseButtonBackground}
        padding='25px 20px 25px 8px'
        borderRadius={0}
        opacity={1}
      >
        <Text
          px='10px'
          fontWeight='bold'
          fontSize='14px'
          color={mobileStepper.collapseButtonTextColor}
          textAlign='start'
          textTransform='uppercase'
        >
          {isReviewStep ? "What I'm booking" : currentCategory.name}
        </Text>
        <ChevronDown
          width={14}
          height={14}
          fill={mobileStepper.collapseButtonIconColor}
          style={{
            transition: 'transform .2s',
            transform: !isCollapsed ? 'scale(-1)' : 'scale(1)'
          }}
        />
      </Flex>
      <Collapse
        in={!isCollapsed}
        style={{
          width: '100%',
          paddingTop: '10px'
        }}
      >
        {showReviewTab && (
          <>
            <Flex
              onClick={onChangeHandler(reviewTab)}
              mb='10px'
              position='relative'
              justifyContent='center'
              padding='13px'
              bg={
                isReviewStep
                  ? mobileStepper.activeBackground
                  : mobileStepper.inactiveBackground
              }
              borderRadius={0}
            >
              <Text
                px='30px'
                fontSize='14px'
                color={
                  isReviewStep
                    ? mobileStepper.activeTextColor
                    : mobileStepper.inactiveTextColor
                }
                fontWeight={isReviewStep ? 'bold' : 'normal'}
                textTransform='uppercase'
              >
                What I&apos;m booking
              </Text>
            </Flex>
          </>
        )}

        {categories.map((tab, index) => {
          const isFirstItem = index === 0 && !showReviewTab;
          const activeTab = tab.id === currentCategory.id;
          const isLastInGroup = tab.name === 'Consultations';
          const shouldUseBottomSpace =
            tab.name.toLowerCase() === categoryExtraSpaceName;

          return (
            <Box key={tab.id} mb={shouldUseBottomSpace ? '10px' : 0}>
              {isLastInGroup && (
                <Divider borderColor={mobileStepper.dividerColor} />
              )}
              <Flex
                onClick={onChangeHandler(tab)}
                mt={isFirstItem ? 0 : '5px'}
                mb='5px'
                padding='13px'
                position='relative'
                justifyContent='center'
                bg={
                  activeTab
                    ? mobileStepper.activeBackground
                    : mobileStepper.inactiveBackground
                }
                data-status={activeTab ? 'active' : 'inactive'}
                borderRadius={0}
              >
                <Text
                  px='30px'
                  fontSize='14px'
                  color={
                    activeTab
                      ? mobileStepper.activeTextColor
                      : mobileStepper.inactiveTextColor
                  }
                  fontWeight={activeTab ? 'bold' : 'normal'}
                  textTransform='uppercase'
                >
                  {tab.name}
                </Text>

                {activeTab && (
                  <CheckIcon
                    width={14}
                    height={14}
                    fill={mobileStepper.activeIconColor}
                    style={{
                      position: 'absolute',
                      right: 20
                    }}
                  />
                )}
              </Flex>
            </Box>
          );
        })}
      </Collapse>
    </Stack>
  );
};

export default ServiceMobileStepper;
